<template>
    <div v-if="isLoaded" class="page-wrap ep">
        <editor-bar :name="node.Name" :version="node.Version" :dirty="dirty" type-label="value engine" @cancel="cancel()" @save="openSave()" @height="barHeight = $event">
            <button type="button" class="ai-btn pb-btn" title="Improve with AI" @click="showAIModal = true">
                <svg viewBox="0 0 24 24" class="ep-fill"><path d="M13 0L3.5 14H10l-1.5 10L20 9h-6.5L15 0h-2z"/></svg><span>Improve with AI</span>
            </button>
        </editor-bar>

        <div :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'">
            <editor-mode-toggle :preview="showPreview" :changed-count="changedSteps.length" changed-label="stages" @edit="closePreview()" @preview="openPreview()" />
            <div v-if="showPreview" class="ep-preview">
                <playbook-view :preview-node="previewNode" :hide-bar="true" :changed-steps="changedSteps" />
            </div>

            <div v-show="!showPreview" class="ep-layout">
                <div class="ep-main">
                    <div class="ep-fmt" :class="{ live: activeEditor }" :style="{ top: barHeight + 'px' }">
                        <span class="ep-fmt-where">{{ activeEditor ? 'Stage ' + (selected + 1) + ' details' : 'Click into a stage\'s details to format them' }}</span>
                        <div v-if="activeEditor" class="ep-fmt-btns" @mousedown="keepEditorFocus">
                            <tip-tap-toolbar :editor="activeEditor" :show-align="true" />
                        </div>
                    </div>

                    <article class="ep-paper">
                        <editor-doc-head :locs="locs" :deps="deps" :types="types" :owner-names="ownerNames" :version="node.Version" @open="showDetails = true" />

                        <value-engine-view :items="items" :editable="true" :selected="selected" :item-key="keyOf" @update:selected="select($event)">
                            <template #add>
                                <button type="button" class="ve-add" @click="addItem(items.length)">
                                    <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg><span>Add stage</span>
                                </button>
                            </template>
                            <template #editor>
                                <section v-if="current" class="ve-ed" :style="current.color ? { '--ve-color': current.color } : {}">
                                    <div class="ve-ed-h">
                                        <span class="ve-n">{{ selected + 1 }}</span>
                                        <span class="ve-ed-lbl">Stage {{ selected + 1 }} of {{ items.length }}</span>
                                        <span class="ve-ed-tools">
                                            <button type="button" aria-label="Move stage left" :disabled="selected == 0" @click="moveItem(selected, -1)"><svg viewBox="0 0 24 24"><path d="M19 12H5M11 18l-6-6 6-6" /></svg></button>
                                            <button type="button" aria-label="Move stage right" :disabled="selected == items.length - 1" @click="moveItem(selected, 1)"><svg viewBox="0 0 24 24"><path d="M5 12h14M13 18l6-6-6-6" /></svg></button>
                                            <button type="button" aria-label="Add a stage after this one" title="Add a stage after this one" @click="addItem(selected + 1)"><svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg></button>
                                            <button type="button" class="del" aria-label="Delete stage" @click="removeItem(selected)"><svg viewBox="0 0 24 24"><path d="M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7V4h6v3" /></svg></button>
                                        </span>
                                    </div>
                                    <div class="ve-ed-grid">
                                        <div>
                                            <label class="ep-lbl" for="ve-title">Title<span>up to 3 lines</span></label>
                                            <textarea id="ve-title" ref="title" class="ve-in big" :rows="lineCount(titleText)" :value="titleText" placeholder="e.g. Freight Request Received" @input="onLines($event, 'title')"></textarea>
                                        </div>
                                        <div>
                                            <label class="ep-lbl" for="ve-sub">Subtitle<span>up to 3 lines</span></label>
                                            <textarea id="ve-sub" class="ve-in" :rows="lineCount(subText)" :value="subText" placeholder="e.g. via Dealer Portal" @input="onLines($event, 'sub')"></textarea>
                                        </div>
                                    </div>
                                    <div class="ve-ed-body">
                                        <label class="ep-lbl">Details<span>shown when someone clicks this stage</span></label>
                                        <tip-tap :key="keyOf(current)" v-model="current.description" :hide-toolbar="true" :show-align="true" @focus="activeEditor = $event" />
                                    </div>
                                    <div class="ve-ed-row">
                                        <div>
                                            <label class="ep-lbl">Image</label>
                                            <div v-if="current.imageURL" class="ve-ed-pic">
                                                <img :src="current.imageURL" alt="Stage image" />
                                                <button type="button" class="ep-x" aria-label="Remove image" @click="current.imageURL = null"><svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg></button>
                                            </div>
                                            <label v-else :class="['ep-chip', { busy: uploading }]">
                                                <input type="file" accept="image/*" hidden @change="onPickImage($event)" />
                                                <svg viewBox="0 0 24 24"><path d="M15 8h.01M12 21H6a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v6M3 16l5-5c.93-.9 2.07-.9 3 0l4 4M14 14l1-1c.67-.64 1.33-.84 2-.6M16 19h6M19 16v6" /></svg>{{ uploading ? 'Uploading…' : 'Add image' }}
                                            </label>
                                        </div>
                                        <div class="grow">
                                            <label class="ep-lbl">Links<span>shown with the details</span></label>
                                            <div v-for="(link, j) in (current.linkMenu || [])" :key="j" class="ve-lrow">
                                                <input v-model="link.label" placeholder="Label, e.g. Dealer Portal" />
                                                <input v-model="link.link" placeholder="https://…" />
                                                <button type="button" aria-label="Remove link" @click="removeLink(j)"><svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg></button>
                                            </div>
                                            <button type="button" class="ep-chip" @click="addLink()">
                                                <svg viewBox="0 0 24 24"><path d="M9 15l6-6M11 6l.46-.54a5 5 0 0 1 7.07 7.07L18 13M13 18l-.4.53a5.07 5.07 0 0 1-7.12 0 4.97 4.97 0 0 1 0-7.07L6 11M16 19h6M19 16v6" /></svg>Add link
                                            </button>
                                        </div>
                                    </div>
                                    <div class="ve-ed-row">
                                        <div>
                                            <label class="ep-lbl" for="ve-color">Color</label>
                                            <div class="ve-color">
                                                <input id="ve-color" type="color" :value="current.color || '#71e2fb'" @input="current.color = ($event.target as HTMLInputElement).value" />
                                                <span class="ve-hint" style="margin:0">Number and top edge of the card</span>
                                            </div>
                                        </div>
                                    </div>
                                </section>
                                <p v-else class="ep-empty">No stages yet. Add one to get started.</p>
                            </template>
                        </value-engine-view>
                    </article>
                </div>

                <aside class="ep-side" aria-label="Outline" :style="{ top: barHeight + 'px' }">
                    <div class="ep-side-h">Outline</div>
                    <button v-for="(item, i) in items" :key="keyOf(item)" type="button" :class="['ep-ol', { on: selected == i }]" @click="select(i)">
                        <span>{{ i + 1 }}</span>{{ stageTitle(item) || 'Untitled stage' }}
                    </button>
                    <editor-activity :comments="comments" :signed-count="signedCount" :required-count="requiredSigners.length" :version="node.Version" />
                </aside>
            </div>
        </div>

        <editor-details-drawer v-if="showDetails" v-model:name="node.Name" v-model:ownerIds="ownerIDs" v-model:locs="locs" v-model:deps="deps" v-model:types="types" type-label="value engine" @close="showDetails = false" />
        <editor-save-dialog v-if="showSave" v-model:status="node.NeedsApprovalType" :version="node.Version" :required-count="requiredSigners.length" :signed-count="signedCount" :busy="disableButton" note-placeholder="e.g. Added the dealer portal link to stage 1" @cancel="showSave = false" @save="submitCheck($event)" />
        <editor-discard-dialog v-if="showDiscard" @keep="showDiscard = false" @discard="leave()" />

        <confirm-delete v-if="showAIConfirm" @confirm="submit()" @cancel="() => { showAIConfirm = false; }" :msg="AIConfirmText"/>
        <ai-improve-panel v-if="showAIModal" node-type="timeline" :slug="node.Slug" :current-draft="{ name: node.Name, items: timelineJSON.items }" @close="showAIModal = false" @apply="d => { onAIDraft(d); showAIModal = true }" @accept="onAIDraft" />
    </div>
</template>

<script lang="ts">
import { defineComponent, toRaw } from 'vue';
import TipTap from '@/components/TipTap.vue';
import TipTapToolbar from '@/components/TipTapToolbar.vue';
import AIImprovePanel from '@/components/AIImprovePanel.vue';
import PlaybookView from '@/views/Playbook.vue';
import ConfirmDelete from '@/components/ConfirmDelete.vue';
import ValueEngineView from '@/components/ValueEngineView.vue';
import EditorBar from '@/components/EditorBar.vue';
import EditorModeToggle from '@/components/EditorModeToggle.vue';
import EditorDocHead from '@/components/EditorDocHead.vue';
import EditorActivity from '@/components/EditorActivity.vue';
import EditorDetailsDrawer from '@/components/EditorDetailsDrawer.vue';
import EditorSaveDialog from '@/components/EditorSaveDialog.vue';
import EditorDiscardDialog from '@/components/EditorDiscardDialog.vue';
import nodeEditor from '@/nodeEditor';

// Stable keys for stages without adding anything to the saved TimelineJSON
const itemKeys = new WeakMap<object, number>();
let nextItemKey = 1;

const TITLE_FIELDS = [ 'lineOne', 'lineTwo', 'lineThree' ];
const SUB_FIELDS = [ 'smallLineOne', 'smallLineTwo', 'smallLineThree' ];

// What counts as a change: the fields the page shows, ignoring '' vs. missing
function normalizeItem(item: any) {
    return {
        title: TITLE_FIELDS.map(f => item[f] || ''),
        sub: SUB_FIELDS.map(f => item[f] || ''),
        description: item.description || '',
        imageURL: item.imageURL || null,
        linkMenu: (item.linkMenu || []).map((l: any) => ({ label: l.label || '', link: l.link || '' })),
        color: item.color || ''
    };
}

function withHttps(url: string): string {
    return url && !url.includes('https://') && !url.includes('http://') ? 'https://' + url : url;
}

export default defineComponent({
    name: 'EditTimeline',
    mixins: [ nodeEditor ],
    components: {
        PlaybookView,
        TipTap,
        TipTapToolbar,
        AiImprovePanel: AIImprovePanel,
        ConfirmDelete,
        ValueEngineView,
        EditorBar,
        EditorModeToggle,
        EditorDocHead,
        EditorActivity,
        EditorDetailsDrawer,
        EditorSaveDialog,
        EditorDiscardDialog
    },
    data() {
        return {
            timelineJSON: { items: [] } as any,
            savedItems: [] as any[],
            selected: 0,
            // The title / subtitle textareas, kept as typed (the item stores them as three line fields)
            titleText: '',
            subText: '',
            activeEditor: null as any,
            uploading: false,
            showAIModal: false
        }
    },
    computed: {
        items(): any[] {
            return this.timelineJSON.items || [];
        },
        current(): any {
            return this.items[this.selected] || null;
        },
        // Stage indexes that differ from the saved version, marked in Preview
        changedSteps(): number[] {
            return this.items.reduce((out: number[], item: any, i: number) => {
                const before = this.savedItems[i];
                if(!before || JSON.stringify(normalizeItem(item)) != JSON.stringify(before)) out.push(i);
                return out;
            }, []);
        }
    },
    beforeRouteLeave(to: any, from: any, next: any) {
        this.guardLeave(next);
    },
    methods: {
        // nodeEditor content hooks
        contentField(): string {
            return 'TimelineJSON';
        },
        loadContent(json: any) {
            this.timelineJSON = json;
            if(!this.timelineJSON.items) this.timelineJSON.items = [];
            this.select(0);
        },
        contentData(): any {
            return this.timelineJSON;
        },
        contentSnapshot(): any {
            return this.items.map(normalizeItem);
        },
        onMarkSaved() {
            this.savedItems = this.items.map(normalizeItem);
        },
        typeTitle(): string {
            return 'Value Engine';
        },
        // Same tidy-up the old editor did on save: links get https://, and empty link rows are dropped
        beforeSave() {
            for(const item of this.items) {
                if(item.linkMenu) {
                    item.linkMenu = item.linkMenu
                        .filter((l: any) => l.link && `${l.link}`.trim())
                        .map((l: any) => ({ ...l, label: l.label || l.link, link: withHttps(`${l.link}`.trim()) }));
                    if(!item.linkMenu.length) item.linkMenu = null;
                }
            }
        },

        keyOf(item: any): number {
            const raw = toRaw(item);
            if(!itemKeys.has(raw)) itemKeys.set(raw, nextItemKey++);
            return itemKeys.get(raw) as number;
        },
        stageTitle(item: any): string {
            return TITLE_FIELDS.map(f => item[f]).filter(line => line && `${line}`.trim()).join(' ');
        },
        select(i: number) {
            this.selected = Math.max(0, Math.min(i, this.items.length - 1));
            this.activeEditor = null;
            const item = this.current;
            this.titleText = item ? this.textOf(item, TITLE_FIELDS) : '';
            this.subText = item ? this.textOf(item, SUB_FIELDS) : '';
        },
        // The three line fields as textarea text, without trailing empty lines
        textOf(item: any, fields: string[]): string {
            const lines = fields.map(f => item[f] || '');
            while(lines.length && !lines[lines.length - 1]) lines.pop();
            return lines.join('\n');
        },
        lineCount(text: string): number {
            return Math.max(1, Math.min(3, text.split('\n').length));
        },
        // Up to 3 lines; each line goes to its own field (lineOne/Two/Three or smallLineOne/Two/Three)
        onLines(e: any, which: string) {
            let lines = e.target.value.split('\n');
            if(lines.length > 3) {
                lines = lines.slice(0, 3);
                e.target.value = lines.join('\n');
            }
            const text = lines.join('\n');
            const fields = which == 'title' ? TITLE_FIELDS : SUB_FIELDS;
            fields.forEach((f, i) => { this.current[f] = lines[i] || ''; });
            if(which == 'title') this.titleText = text;
            else this.subText = text;
        },
        addItem(index: number) {
            const neighbor = this.items[index - 1] || this.items[index];
            this.timelineJSON.items.splice(index, 0, {
                lineOne: '',
                lineTwo: '',
                lineThree: '',
                color: neighbor?.color || '#71E2FB',
                description: ''
            });
            this.select(index);
            this.$nextTick(() => {
                const title = this.$refs.title as HTMLTextAreaElement;
                if(title) title.focus();
                const card = document.getElementById('ve-card-' + index);
                if(card) card.scrollIntoView({ block: 'nearest', inline: 'center' });
            });
        },
        moveItem(i: number, dir: number) {
            const items = this.timelineJSON.items;
            const j = i + dir;
            if(j < 0 || j >= items.length) return;
            [items[i], items[j]] = [items[j], items[i]];
            this.select(j);
        },
        removeItem(i: number) {
            this.timelineJSON.items.splice(i, 1);
            this.select(Math.max(0, i - 1));
        },
        addLink() {
            if(!this.current.linkMenu) this.current.linkMenu = [];
            this.current.linkMenu.push({ label: '', link: '' });
        },
        removeLink(j: number) {
            this.current.linkMenu.splice(j, 1);
            if(!this.current.linkMenu.length) this.current.linkMenu = null;
        },
        async onPickImage(e: any) {
            const file = e.target.files && e.target.files[0];
            e.target.value = '';
            if(!file) return;
            const item = this.current;
            this.uploading = true;
            const url = await this.uploadImage(file);
            if(url) item.imageURL = url;
            this.uploading = false;
        },
        // Clicking a toolbar button shouldn't take the cursor out of the details being formatted
        keepEditorFocus(e: any) {
            if(e.target.tagName != 'INPUT' && e.target.tagName != 'SELECT') e.preventDefault();
        },
        onAIDraft(draft: any) {
            // Preserve link menus, images and exact colors from existing
            // steps whose lineOne still matches (then by position as fallback)
            const oldItems = [...this.items];
            this.node.Name = draft.name;
            // Targeted edits ("Improve with AI" chat) already carry each step's own links, images and color
            this.timelineJSON.items = draft.preserved ? draft.items : draft.items.map((item: any, i: number) => {
                const match = oldItems.find((o: any) => o.lineOne && item.lineOne && o.lineOne.trim() == item.lineOne.trim()) || oldItems[i];
                return {
                    ...item,
                    linkMenu: match?.linkMenu || undefined,
                    imageURL: match?.imageURL || undefined,
                    color: match?.color || item.color
                };
            });
            this.select(0);
            this.showAIModal = false;
        }
    }
});
</script>
